import { useEffect, useMemo, useRef, useState } from "react"
import { ActivityIndicator, ScrollView, TextStyle, View, ViewStyle } from "react-native"

import { usePostHog } from "posthog-react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { getLoginRole } from "types/role"

import { Button, Text } from "src/components/Ignite"
import { Loading } from "src/components/Loading"
import { useAuth } from "src/context/AuthContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useCleanupThenLogout, useLogoutStatus, useSponsorListQuery } from "src/queries/useUser"
import { SponsorInfo } from "src/services/api/api.types"
import { ThemedStyle } from "src/theme"
import { Alert } from "./Alert"

const DEFAULT_TARGET_CHANNEL_MISSING_MESSAGE = "Channel not found or unavailable."

interface SelectSponsorProps {
  needsConfirmation?: boolean
  shouldCloseConversation?: boolean
  closeConversation?: () => Promise<boolean>
  /** Called before sponsor switch starts - useful for hiding avatar */
  onSwitchStart?: () => void
  /** Called after sponsor switch fails - useful for restoring avatar */
  onSwitchError?: () => void
  /** Called after successful sponsor switch - if provided, overrides default navigation */
  onSwitchComplete?: () => void
  /** Skip adding safe area insets (when used inside a container that already handles them) */
  skipSafeAreaInsets?: boolean
  style?: ViewStyle
  /** When set, attempts to switch to this sponsor id once the sponsor list has loaded (deeplink flow). */
  targetChannelId?: string
  /** Shown when `targetChannelId` is not in the user's sponsor list. */
  targetChannelMissingMessage?: string
  /** Invoked when `targetChannelId` is not in the sponsor list (after showing the banner). */
  onTargetChannelNotFound?: (sponsors: SponsorInfo[]) => void
}

export function SelectSponsor({
  needsConfirmation = false,
  shouldCloseConversation = false,
  closeConversation = async () => false,
  onSwitchStart,
  onSwitchError,
  onSwitchComplete,
  skipSafeAreaInsets = false,
  style,
  targetChannelId,
  targetChannelMissingMessage = DEFAULT_TARGET_CHANNEL_MISSING_MESSAGE,
  onTargetChannelNotFound,
}: SelectSponsorProps) {
  const safeInsets = useSafeAreaInsets()
  const { theme, themed } = useAppTheme()
  const posthog = usePostHog()
  const { api, updateApiHostFromBackend, switchSponsor, sponsorId, pendingLoginSponsors } =
    useAuth()

  // Calculate margins - skip if parent already handles safe area
  const topMargin = skipSafeAreaInsets ? 0 : safeInsets.top
  const bottomMargin = skipSafeAreaInsets ? 0 : safeInsets.bottom

  // Source sponsor list from in-memory pendingLoginSponsors (post-login happy path),
  // falling back to useSponsorListQuery for in-app switches and post-login reloads.
  const sponsorListQuery = useSponsorListQuery({ enabled: !pendingLoginSponsors })
  const { mutate: triggerAutoLogout } = useCleanupThenLogout()
  const { isLoggingOut } = useLogoutStatus()
  const [selectedSponsor, setSelectedSponsor] = useState<SponsorInfo | null>()
  const [pendingSponsorId, setPendingSponsorId] = useState<string | null>(null)
  const [switchingSponsorId, setSwitchingSponsorId] = useState<string | null>(null)
  const [targetChannelBanner, setTargetChannelBanner] = useState<string | null>(null)
  /** When false with a deeplink target, show loading instead of the channel list until auto-switch finishes or errors. */
  const [revealChannelList, setRevealChannelList] = useState(() => !targetChannelId)
  const autoTargetAttemptRef = useRef<string | null>(null)
  const autoTargetSwitchFailedRef = useRef(false)

  // Use in-memory list if available, otherwise fall back to network-fetched list.
  const sponsorList = useMemo(
    () => pendingLoginSponsors ?? sponsorListQuery.data ?? [],
    [pendingLoginSponsors, sponsorListQuery.data],
  )
  const isLoadingSponsors =
    !pendingLoginSponsors && (sponsorListQuery.isPending || sponsorListQuery.isFetching)

  useEffect(() => {
    autoTargetAttemptRef.current = null
    autoTargetSwitchFailedRef.current = false
    setTargetChannelBanner(null)
    setRevealChannelList(!targetChannelId)
  }, [targetChannelId])

  // If the fallback sponsor-list query fails (only runs when pendingLoginSponsors is null),
  // the session is unrecoverable — auto-logout so the user lands back on /login.
  useEffect(() => {
    if (sponsorListQuery.isError && !isLoggingOut) {
      console.warn(
        "[SelectSponsor] sponsor list fetch failed — auto-logout",
        sponsorListQuery.error,
      )
      triggerAutoLogout()
    }
  }, [sponsorListQuery.isError, sponsorListQuery.error, isLoggingOut, triggerAutoLogout])

  // Update selected sponsor when we enter this screen or when data loads
  useEffect(() => {
    if (sponsorList.length > 0 && sponsorId) {
      const foundSponsor = sponsorList.find((sponsor) => sponsor.sponsor_id === sponsorId)
      setSelectedSponsor(foundSponsor!)
    }
  }, [sponsorList, sponsorId])

  function handleSwitchSponsorPressed(sponsor: SponsorInfo) {
    if (sponsor.sponsor_id === sponsorId) {
      onSwitchComplete?.()
      return
    }

    setPendingSponsorId(sponsor.sponsor_id)

    if (needsConfirmation) {
      Alert.alert("Confirm Channel Change", "Change channel to " + sponsor.sponsor_name + "?", [
        {
          text: "Cancel",
          style: "cancel",
          onPress: () => {
            setPendingSponsorId(null)
          },
        },
        {
          text: "Confirm",
          style: "default",
          onPress: () => handleSwitchSponsor(sponsor),
        },
      ])
    } else {
      handleSwitchSponsor(sponsor)
    }
  }

  async function handleSwitchSponsor(sponsor: SponsorInfo): Promise<boolean> {
    setSwitchingSponsorId(sponsor.sponsor_id)
    onSwitchStart?.()

    const previousSponsor = selectedSponsor!
    const targetApiUrl = sponsor.environment_url

    // Determine whether this is a login bootstrap (first sponsor pick after
    // multi-sponsor login or a post-login page reload without a context token)
    // or an in-app switch (context token already present).
    const isLoginBootstrap = !!pendingLoginSponsors

    // Cross-environment switches must also use login bootstrap — the current
    // context token was issued by a different backend and would be rejected
    // with 401 if sent to the target environment.
    const isCrossEnvironment =
      !isLoginBootstrap && selectedSponsor?.environment_url !== sponsor.environment_url

    try {
      // First, cleanly close the current conversation if needed.
      // This must happen BEFORE acquiring the session-switch lock because closeConversation
      // makes API calls to the current host — acquiring the lock first would deadlock.
      if (shouldCloseConversation) {
        try {
          console.log("[handleSwitchSponsor] Closing current conversation before sponsor switch")
          await closeConversation()
          console.log("[handleSwitchSponsor] Current conversation closed successfully")
        } catch (error) {
          console.error("[handleSwitchSponsor] Error closing current conversation:", error)
        }
      }

      // Update the api base URL immediately (so the switchContext request targets
      // the right host) but skip persisting to MMKV for now (skipPersist: true).
      // Persisting would call setApiHost → trigger a React re-render → recreate
      // the `api` useMemo instance, breaking the session-switch lock that
      // switchSponsor will acquire internally.
      // We persist the host after the switch completes below.
      console.log("[handleSwitchSponsor] Updating API URL to:", targetApiUrl)
      const updateApiUrlResult = updateApiHostFromBackend(targetApiUrl, { skipPersist: true })
      if (updateApiUrlResult.kind === "error") {
        throw new Error(
          "[handleSwitchSponsor] Error updating API host:\n" + updateApiUrlResult.message,
        )
      }
      console.log(
        "[handleSwitchSponsor] API URL updated, current baseURL:",
        api.apisauce.getBaseURL(),
      )

      // Delegate to AuthContext.switchSponsor which manages the session-switch
      // lock internally around POST /context/switch and performs all data-cleanup,
      // getRoleInfo, and setUserSelectedSession steps.
      await switchSponsor(sponsor.sponsor_id, {
        isLogin: isLoginBootstrap || isCrossEnvironment,
        role: getLoginRole(sponsor.default_role),
      })

      // Persist the resolved host now that switchContext has completed and the
      // context token is stored. This triggers a re-render / api recreation, but
      // queries that fire after this point will have the fresh token available.
      updateApiHostFromBackend(targetApiUrl)

      setSelectedSponsor(sponsor)

      if (onSwitchComplete) {
        onSwitchComplete()
      }
      return true
    } catch (error) {
      console.error("[handleSwitchSponsor] Error:", error)
      // Revert the api base URL to the previous sponsor. The original host was
      // already persisted before this switch attempt (we used skipPersist:true
      // above), so no MMKV write is needed on the revert path.
      if (previousSponsor?.environment_url) {
        const resetApiUrlResult = updateApiHostFromBackend(previousSponsor.environment_url, {
          skipPersist: true,
        })
        if (resetApiUrlResult.kind === "error") {
          console.error(
            "[handleSwitchSponsor] Error resetting to previous sponsor API host:",
            resetApiUrlResult.message,
          )
        }
      }
      onSwitchError?.()
      return false
    } finally {
      setPendingSponsorId(null)
      setSwitchingSponsorId(null)
    }
  }

  // Channel deeplink: auto-switch once sponsor list is ready (see handleSwitchSponsor above).
  useEffect(() => {
    if (!targetChannelId) return
    if (isLoadingSponsors || sponsorListQuery.isError) return
    if (autoTargetSwitchFailedRef.current) return
    if (autoTargetAttemptRef.current === targetChannelId) return

    const sponsor = sponsorList.find((s) => s.sponsor_id === targetChannelId)
    if (!sponsor) {
      autoTargetAttemptRef.current = targetChannelId
      setTargetChannelBanner(targetChannelMissingMessage)
      setRevealChannelList(true)
      posthog?.capture("channel_link_access_denied", { channelId: targetChannelId })
      onTargetChannelNotFound?.(sponsorList)
      return
    }

    if (sponsor.sponsor_id === sponsorId) {
      autoTargetAttemptRef.current = targetChannelId
      onSwitchComplete?.()
      return
    }

    autoTargetAttemptRef.current = targetChannelId
    handleSwitchSponsor(sponsor).then((ok) => {
      if (!ok) {
        autoTargetSwitchFailedRef.current = true
        autoTargetAttemptRef.current = null
        setRevealChannelList(true)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleSwitchSponsor is stable enough for this flow; listing it causes redundant attempts
  }, [
    targetChannelId,
    isLoadingSponsors,
    sponsorListQuery.isError,
    sponsorList,
    sponsorId,
    targetChannelMissingMessage,
    onSwitchComplete,
    onTargetChannelNotFound,
    posthog,
  ])

  function isSelectedSponsor(sponsor: SponsorInfo) {
    const highlightedSponsorId = pendingSponsorId ?? selectedSponsor?.sponsor_id
    return highlightedSponsorId === sponsor.sponsor_id
  }

  const showDeeplinkBlockingLoad = !!targetChannelId && !revealChannelList && !targetChannelBanner

  if (sponsorListQuery.isError || isLoadingSponsors) {
    // When the query has errored (or we're actively logging out as a
    // result of that error), surface "Signing you out…" so the user
    // understands the system is reacting — not stuck. The auto-logout
    // effect above will redirect to /login once auth state clears.
    const isFailing = sponsorListQuery.isError || isLoggingOut
    const message = isFailing ? "Signing you out…" : "Loading…"
    return (
      <View style={[{ ...$flex, marginTop: topMargin, marginBottom: bottomMargin }, style]}>
        <View style={themed($container)}>
          <Loading message={message} />
        </View>
      </View>
    )
  }

  if (showDeeplinkBlockingLoad) {
    return (
      <View style={[{ ...$flex, marginTop: topMargin, marginBottom: bottomMargin }, style]}>
        <View style={themed($container)}>
          <Loading message="Opening channel…" />
        </View>
      </View>
    )
  }

  return (
    <View style={[{ ...$flex, marginTop: topMargin, marginBottom: bottomMargin }, style]}>
      <View style={themed($container)}>
        <ScrollView
          style={themed($scrollView)}
          contentContainerStyle={themed($menu)}
          showsVerticalScrollIndicator={true}
        >
          {targetChannelBanner ? (
            <View style={themed($banner)} accessibilityRole="alert">
              <Text text={targetChannelBanner} style={themed($bannerText)} />
            </View>
          ) : null}
          <View style={themed($menuOptions)}>
            {sponsorList.map((sponsor) => {
              const isSwitching = switchingSponsorId === sponsor.sponsor_id
              const isSelected = isSelectedSponsor(sponsor)
              return (
                <Button
                  key={sponsor.sponsor_id}
                  text={sponsor.sponsor_name}
                  onPress={() => handleSwitchSponsorPressed(sponsor)}
                  preset="subtle"
                  disabled={isSwitching || switchingSponsorId !== null}
                  style={[themed($sponsorButton), isSelected && themed($selectedSponsorButton)]}
                  textStyle={[
                    themed($sponsorButtonText),
                    isSelected && themed($selectedSponsorButtonText),
                  ]}
                  RightAccessory={
                    isSwitching
                      ? () => (
                          <ActivityIndicator
                            size="small"
                            style={themed($spinner)}
                            color={theme.colors.menuButtonText}
                          />
                        )
                      : undefined
                  }
                />
              )
            })}
          </View>
        </ScrollView>
      </View>
    </View>
  )
}

const $flex: ViewStyle = { flex: 1 }

const $scrollView: ThemedStyle<ViewStyle> = () => ({
  flex: 1,
})

const $container: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flex: 1,
  backgroundColor: colors.background,
})

const $menu: ThemedStyle<ViewStyle> = () => ({
  paddingHorizontal: 24,
  paddingBottom: 16,
  alignItems: "center",
})

const $banner: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  width: "100%",
  maxWidth: 600,
  alignSelf: "center",
  marginBottom: spacing.md,
  padding: spacing.md,
  borderRadius: 8,
  backgroundColor: colors.errorBackground,
})

const $bannerText: ThemedStyle<TextStyle> = ({ colors }) => ({
  textAlign: "center",
  color: colors.text,
})

const $menuOptions: ThemedStyle<ViewStyle> = () => ({
  width: "100%",
  maxWidth: 600,
  alignSelf: "center",
  gap: 8,
})

const $sponsorButton: ThemedStyle<ViewStyle> = () => ({
  width: "100%",
})

const $selectedSponsorButton: ThemedStyle<ViewStyle> = ({ colors }) => ({
  borderWidth: 2,
  borderColor: colors.menuButtonText,
})

const $sponsorButtonText: ThemedStyle<TextStyle> = () => ({
  textAlign: "center",
})

const $selectedSponsorButtonText: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.menuButtonText,
})

const $spinner: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  marginLeft: spacing.sm,
})
